import Image from "next/image"
import React, { useEffect, useMemo, useState } from "react"
import { CloseButton } from "react-bootstrap"
import usePortal from "react-useportal"
import { v4 as uuid } from "uuid"

export type ToastItemProps = {
  titleText: string
  text?: React.ReactNode
  footer?: string
  icon?: "info" | "success" | "warning" | "error"
  timeout?: number
  // the (optional) id can be shared among toasts
  // only the most recent toast will be shown for each id
  // this is useful when the new information
  // renders the previously shown information outdated
  // e.g. upload started -> upload complete
  id?: string
  timeoutId?: number // id to clear previously scheduled window.setTimeout
  onClose?: () => void
  // the `notification` variant looks like the preview in the notification composer
  variant?: "notification" | "toast"
}

export type UseToastProps = {
  show: (toastProps: ToastItemProps) => void
  hide: (id: string) => void
  props: ToastProps
}

export type ToastProps = {
  toastList: ToastItemProps[]
}

export const useToast = (): UseToastProps => {
  const [newToast, showToast] = useState<ToastItemProps | undefined>(undefined)
  const [toastList, setToastList] = useState<ToastItemProps[]>([])
  const hide = (id: string) =>
    setToastList((toastList) => toastList.filter((toast) => toast.id !== id))

  useEffect(() => {
    if (newToast) {
      const toastId = newToast.id ?? uuid()
      const previousToast = toastList.find((toast) => toast.id === toastId)
      if (previousToast) {
        window.clearTimeout(previousToast.timeoutId)
      }
      let timeoutId: number | undefined
      if (newToast.timeout !== Infinity) {
        timeoutId = window.setTimeout(() => {
          hide(toastId)
        }, newToast.timeout || 3000)
      }
      setToastList((toastList) => [
        { ...newToast, id: toastId, timeoutId, onClose: () => hide(toastId) },
        ...toastList.filter((toast) => !(newToast.id && newToast.id === toast.id)),
      ])
      showToast(undefined)
      return () => showToast(undefined)
    }
  }, [newToast, toastList])
  return useMemo(
    () => ({
      show: (toastItem: ToastItemProps) => showToast(toastItem),
      hide,
      props: { toastList },
    }),
    [toastList],
  )
}

const ToastItem = ({ titleText, text, footer, icon, onClose, variant }: ToastItemProps) => {
  if (variant === "notification") {
    return (
      <div
        className="border border-muted rounded-5 p-3 align-self-center bg-white"
        style={{ width: 300 }}
      >
        <div className="hstack position-relative">
          <div
            className="flex-shrink-0 flex-grow-0"
            style={{ flexBasis: 32, height: 32, width: 32 }}
          >
            <Image src="/logos/logo.png" alt="PlayerDash app logo" height={32} width={32} />
          </div>
          <div className="vstack gap-1 ms-3 flex-shrink-1 flex-grow-1">
            <span className="fw-bold">{titleText}</span>
            <span style={{ overflowWrap: "anywhere" }}>{text}</span>
          </div>
          <CloseButton className="position-absolute top-0 end-0" onClick={onClose} />
        </div>
      </div>
    )
  }
  const messageWithIcon = (text: string, icon?: string) => {
    switch (icon) {
      case "success":
        return (
          <span className="text-success me-auto lead">
            <i className="bi bi-check-circle me-2" />
            {text || ""}
          </span>
        )
      case "warning":
        return (
          <span className="text-warning me-auto lead">
            <i className="bi bi-exclamation-circle me-2" />
            {text || ""}
          </span>
        )
      case "error":
        return (
          <span className="text-danger me-auto lead">
            <i className="bi bi-x-circle me-2" />
            {text || ""}
          </span>
        )
      default:
        return (
          <span className="text-info me-auto lead">
            <i className="bi bi-info-circle me-2" />
            {text || ""}
          </span>
        )
    }
  }
  return (
    <div className="toast show" role="alert" aria-live="assertive" aria-atomic="true" id="toast">
      <div className="toast-header">
        {messageWithIcon(titleText, icon)}
        {footer && <small>{footer}</small>}
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="toast"
          aria-label="Close"
          onClick={() => onClose?.()}
        />
      </div>
      <div className="toast-body">{text}</div>
    </div>
  )
}

export const Toast = ({ toastList }: ToastProps) => {
  const { Portal } = usePortal({
    bindTo:
      (typeof document !== "undefined" && document && document.getElementById("toast-container")) ||
      undefined,
  })
  return (
    <>
      <Portal>
        {toastList?.map((toastProps: ToastItemProps, index: number) => (
          <ToastItem key={index.toString()} {...toastProps} />
        ))}
      </Portal>
    </>
  )
}

export const ToastContainer = () => {
  return (
    <div id="toast-container" className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1056 }} />
  )
}
